import * as THREE from 'three';
import type { FeatureFace } from '../FeatureFace';
import { FeatureFaceSpatialAccelerationEnsurer } from './FeatureFaceSpatialAccelerationEnsurer';
import type { FeatureFaceSpatialHitInfo } from './featureFaceSpatialQueryTypes';

/**
 * BVH 点→面投影结果。
 */
export interface FeatureFaceBvhProjectionResult {
    /** 曲面上的最近点（世界坐标）。 */
    point: THREE.Vector3;
    /** 最近点处的世界法向。 */
    normal: THREE.Vector3;
    /** 与输入点的平方距离。 */
    distanceSq: number;
}

/**
 * BVH 点→面最近点结果。
 *
 * 设计说明：测量最短距离只需要最近点，不应因为法向重建失败丢弃有效距离结果。
 */
export interface FeatureFaceBvhClosestPointResult {
    /** 曲面上的最近点（世界坐标）。 */
    point: THREE.Vector3;
    /** 与输入点的平方距离。 */
    distanceSq: number;
}

/**
 * BVH 点→面投影可选参数。
 */
export interface FeatureFaceBvhProjectionOptions {
    /** 最大搜索距离；用于面↔面精化阶段的 BVH 剪枝。 */
    maxThreshold?: number;
}

/** 复用世界→局部矩阵，避免热路径频繁分配。 */
const worldToLocalMatrix = new THREE.Matrix4();

/** 复用局部→世界矩阵，避免热路径频繁分配。 */
const localToWorldMatrix = new THREE.Matrix4();

/** 复用局部查询点。 */
const localQueryPoint = new THREE.Vector3();

/** 复用局部最近点。 */
const localClosestPoint = new THREE.Vector3();

/** 复用 BVH 命中对象。 */
const bvhHitInfo: FeatureFaceSpatialHitInfo = {
    point: new THREE.Vector3(),
    distance: 0,
    faceIndex: 0,
};

/** 复用法向矩阵。 */
const normalMatrix = new THREE.Matrix3();

/** 复用三角形与重心坐标。 */
const triangle = new THREE.Triangle();
const barycentric = new THREE.Vector3();

/**
 * BVH 点→面最近点内部命中。
 */
interface FeatureFaceBvhClosestPointHit {
    /** 曲面上的最近点（世界坐标）。 */
    point: THREE.Vector3;
    /** 曲面上的最近点（面局部坐标）。 */
    localPoint: THREE.Vector3;
    /** 与输入点的平方距离。 */
    distanceSq: number;
    /** 命中三角形索引。 */
    faceIndex: number;
}

/**
 * 归一化三维向量。
 *
 * @param value 待归一化向量。
 * @returns 单位向量；退化时返回 `null`。
 */
function normalizeVec3(value: THREE.Vector3): THREE.Vector3 | null {
    const length = value.length();
    if (!Number.isFinite(length) || length < 1e-12) {
        return null;
    }
    return value.clone().normalize();
}

/**
 * 读取缓冲属性中的三维向量。
 *
 * @param attribute 缓冲属性。
 * @param index 顶点索引。
 * @returns 顶点向量。
 */
function readBufferVector3(attribute: THREE.BufferAttribute, index: number): THREE.Vector3 {
    return new THREE.Vector3(
        attribute.getX(index),
        attribute.getY(index),
        attribute.getZ(index),
    );
}

/**
 * 读取三角形三个顶点索引。
 *
 * @param geometry 面代理几何。
 * @param triangleIndex 三角形索引。
 * @returns 三顶点索引；无法读取时返回 `null`。
 */
function readTriangleVertexIndices(
    geometry: THREE.BufferGeometry,
    triangleIndex: number,
): [number, number, number] | null {
    const indexAttribute = geometry.getIndex();
    if (indexAttribute instanceof THREE.BufferAttribute) {
        const cursor = triangleIndex * 3;
        if (cursor + 2 >= indexAttribute.count) {
            return null;
        }
        return [
            Number(indexAttribute.getX(cursor)),
            Number(indexAttribute.getX(cursor + 1)),
            Number(indexAttribute.getX(cursor + 2)),
        ];
    }

    const cursor = triangleIndex * 3;
    const positionAttribute = geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || cursor + 2 >= positionAttribute.count) {
        return null;
    }
    return [cursor, cursor + 1, cursor + 2];
}

/**
 * 基于 BVH 命中三角形重建世界法向。
 *
 * @param entity 特征面代理。
 * @param triangleIndex 命中三角形索引。
 * @param localClosest 局部最近点。
 * @returns 世界法向；退化时返回 `null`。
 */
function buildWorldNormalFromBvhTriangleHit(
    entity: FeatureFace,
    triangleIndex: number,
    localClosest: THREE.Vector3,
): THREE.Vector3 | null {
    const geometry = entity.geometry;
    const positionAttribute = geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return null;
    }

    const vertexIndices = readTriangleVertexIndices(geometry, triangleIndex);
    if (!vertexIndices) {
        return null;
    }

    const [indexA, indexB, indexC] = vertexIndices;
    const localA = readBufferVector3(positionAttribute, indexA);
    const localB = readBufferVector3(positionAttribute, indexB);
    const localC = readBufferVector3(positionAttribute, indexC);

    triangle.set(localA, localB, localC);
    if (triangle.getArea() <= 1e-6) {
        return null;
    }

    normalMatrix.getNormalMatrix(entity.matrixWorld);

    let worldNormal = triangle.getNormal(new THREE.Vector3()).applyMatrix3(normalMatrix).normalize();
    const normalAttribute = geometry.getAttribute('normal');
    if (normalAttribute instanceof THREE.BufferAttribute) {
        const barycoord = triangle.getBarycoord(localClosest, barycentric);
        if (barycoord !== null) {
            const normalA = readBufferVector3(normalAttribute, indexA);
            const normalB = readBufferVector3(normalAttribute, indexB);
            const normalC = readBufferVector3(normalAttribute, indexC);
            const blendedNormal = normalA.multiplyScalar(barycoord.x)
                .add(normalB.multiplyScalar(barycoord.y))
                .add(normalC.multiplyScalar(barycoord.z));
            if (blendedNormal.lengthSq() > 1e-12) {
                worldNormal = blendedNormal.applyMatrix3(normalMatrix).normalize();
            }
        }
    }

    return normalizeVec3(worldNormal);
}

/**
 * 使用 BVH 求世界坐标查询点到特征面三角网格的最近点命中。
 *
 * @param entity 特征面代理。
 * @param targetPointWorld 世界坐标查询点。
 * @param options 可选剪枝参数。
 * @returns 最近点命中；无法求解时返回 `null`。
 */
function queryClosestPointHitOnFeatureFaceMeshViaBvh(
    entity: FeatureFace,
    targetPointWorld: THREE.Vector3,
    options?: FeatureFaceBvhProjectionOptions,
): FeatureFaceBvhClosestPointHit | null {
    const spatialTree = FeatureFaceSpatialAccelerationEnsurer.ensure(entity);
    if (!spatialTree) {
        return null;
    }

    entity.updateWorldMatrix(true, false);
    worldToLocalMatrix.copy(entity.matrixWorld).invert();
    localQueryPoint.copy(targetPointWorld).applyMatrix4(worldToLocalMatrix);

    const maxThreshold = options?.maxThreshold ?? Number.POSITIVE_INFINITY;
    const hit = spatialTree.closestPointToPoint(
        localQueryPoint,
        bvhHitInfo,
        0,
        maxThreshold,
    );
    if (!hit) {
        return null;
    }

    localClosestPoint.copy(hit.point);
    localToWorldMatrix.copy(entity.matrixWorld);
    const worldClosestPoint = localClosestPoint.clone().applyMatrix4(localToWorldMatrix);

    return {
        point: worldClosestPoint,
        localPoint: localClosestPoint.clone(),
        distanceSq: worldClosestPoint.distanceToSquared(targetPointWorld),
        faceIndex: hit.faceIndex,
    };
}

/**
 * 使用 BVH 求世界坐标查询点到特征面三角网格的最近点。
 *
 * 设计说明：
 * 1. 该函数只返回最近点，不要求命中三角形能重建稳定法向；
 * 2. 测量线、面面近似等距离链路应优先使用该函数，避免有效最近点被法向退化误判丢弃；
 * 3. 参考面/参考点等需要法向语义的功能继续使用 {@link queryClosestPointOnFeatureFaceMeshViaBvh}。
 *
 * @param entity 特征面代理。
 * @param targetPointWorld 世界坐标查询点。
 * @param options 可选剪枝参数。
 * @returns 最近点结果；无法求解时返回 `null`。
 */
export function queryClosestPointOnlyOnFeatureFaceMeshViaBvh(
    entity: FeatureFace,
    targetPointWorld: THREE.Vector3,
    options?: FeatureFaceBvhProjectionOptions,
): FeatureFaceBvhClosestPointResult | null {
    const hit = queryClosestPointHitOnFeatureFaceMeshViaBvh(entity, targetPointWorld, options);
    if (!hit) {
        return null;
    }

    return {
        point: hit.point,
        distanceSq: hit.distanceSq,
    };
}

/**
 * 使用 BVH 将世界坐标查询点投影到特征面三角网格。
 *
 * @param entity 特征面代理。
 * @param targetPointWorld 世界坐标查询点。
 * @param options 可选剪枝参数。
 * @returns 投影结果；无法求解时返回 `null`。
 */
export function queryClosestPointOnFeatureFaceMeshViaBvh(
    entity: FeatureFace,
    targetPointWorld: THREE.Vector3,
    options?: FeatureFaceBvhProjectionOptions,
): FeatureFaceBvhProjectionResult | null {
    const hit = queryClosestPointHitOnFeatureFaceMeshViaBvh(entity, targetPointWorld, options);
    if (!hit) {
        return null;
    }

    const worldNormal = buildWorldNormalFromBvhTriangleHit(entity, hit.faceIndex, hit.localPoint);
    if (!worldNormal) {
        return null;
    }

    return {
        point: hit.point,
        normal: worldNormal,
        distanceSq: hit.distanceSq,
    };
}
